import { isDefinedError, safe } from "@orpc/client"
import { createFileRoute } from "@tanstack/react-router"
import { Banknote, FileCheck, ListChecks, Wallet } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import {
	declarationStatusPresentation,
	EmptyLine,
	FLUSH_TABLE_CLASS,
	Money,
	SkeletonRows,
	StatusView,
	SummaryGroup,
	SummaryRow,
	WRAPPING_PAGE_HEADER_CLASS,
} from "@/components/accounting/accounting-ui"
import {
	EMPTY_VALUE,
	formatRate,
} from "@/components/accounting/reconciliation-format"
import {
	DetailPanel,
	DetailPanelSection,
	PageHeader,
	Panel,
} from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot, StatusTag } from "@/components/ui/status"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import {
	useExportVatDeclaration,
	useMarkVatDeclarationFiled,
	useRecordVatDeclarationPayment,
	useVatDeclaration,
} from "@/queries/marketplace/use-vat-declarations"

export const Route = createFileRoute(
	"/accounting/vat-declarations/$declarationId",
)({
	component: VatDeclarationDetailPage,
})

function VatDeclarationDetailPage() {
	const { declarationId } = Route.useParams()
	const query = useVatDeclaration(declarationId)
	const markFiled = useMarkVatDeclarationFiled()
	const recordPayment = useRecordVatDeclarationPayment()
	const exportMut = useExportVatDeclaration()

	if (query.isLoading) {
		return (
			<div className="flex flex-col gap-6 p-4 sm:p-6">
				<Skeleton className="h-7 w-64" />
				<Panel>
					<SkeletonRows rows={6} />
				</Panel>
			</div>
		)
	}
	if (query.isError || !query.data) {
		return (
			<p className="p-4 text-bad sm:p-6">Could not load this declaration.</p>
		)
	}

	const {
		declaration,
		supplies,
		corrections,
		information,
		checks,
		warnings,
		remittances,
	} = query.data

	const handleExport = async (format: "xlsx" | "csv") => {
		const [error, result] = await safe(
			exportMut.mutateAsync({ declarationId, format }),
		)
		if (error) {
			toast.error("Export failed.")
			return
		}
		const a = document.createElement("a")
		a.href = result.downloadUrl
		a.download = result.filename
		a.rel = "noreferrer"
		document.body.appendChild(a)
		a.click()
		a.remove()
	}

	const alreadyFiled =
		declaration.status === "filed" || declaration.status === "paid"

	return (
		<div className="flex flex-col gap-6 p-4 sm:p-6">
			<PageHeader
				className={WRAPPING_PAGE_HEADER_CLASS}
				title={
					<span className="inline-flex items-center gap-3">
						<span className="font-mono">
							{declaration.scheme} · {declaration.filingPeriod}
						</span>
						<StatusView
							presentation={declarationStatusPresentation(
								declaration.status,
								false,
							)}
							className="text-sm font-medium tracking-normal"
						/>
					</span>
				}
				description={`Due ${new Date(declaration.dueDate).toLocaleDateString()}`}
				actions={
					<>
						<Button variant="outline" onClick={() => handleExport("xlsx")}>
							Export XLSX
						</Button>
						<Button variant="outline" onClick={() => handleExport("csv")}>
							Export CSV
						</Button>
					</>
				}
			/>

			<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
				<div className="flex min-w-0 flex-col gap-4">
					{warnings.length > 0 && (
						<div className="flex flex-col gap-1 rounded-lg bg-bad-soft px-4 py-3 text-bad">
							{warnings.map((w) => (
								<p key={w}>{w}</p>
							))}
						</div>
					)}

					<LinePanel title="Supplies" lines={supplies} />
					{corrections.length > 0 && (
						<LinePanel title="Corrections" lines={corrections} />
					)}
					{information.length > 0 && (
						<LinePanel title="Information" lines={information} />
					)}

					<DetailPanel title="Checks" icon={ListChecks} flush>
						{checks.length === 0 ? (
							<EmptyLine className="px-4 py-3">No checks.</EmptyLine>
						) : (
							<Table className={FLUSH_TABLE_CLASS}>
								<TableHeader>
									<TableRow>
										<TableHead>Check</TableHead>
										<TableHead>Passed</TableHead>
										<TableHead>Detail</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{checks.map((c) => (
										<TableRow key={c.check}>
											<TableCell>{c.check}</TableCell>
											<TableCell>
												{c.passed ? (
													<StatusDot tone="ok">Pass</StatusDot>
												) : (
													<StatusTag tone="bad">Fail</StatusTag>
												)}
											</TableCell>
											<TableCell className="text-muted-foreground">
												{c.detail}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
					</DetailPanel>

					<DetailPanel title="Remittances" icon={Banknote} flush>
						{remittances.length === 0 ? (
							<p className="px-4 py-3 text-meta">No payments recorded yet.</p>
						) : (
							<Table className={FLUSH_TABLE_CLASS}>
								<TableHeader>
									<TableRow>
										<TableHead>Filed</TableHead>
										<TableHead>Reference</TableHead>
										<TableHead className="text-right">EUR</TableHead>
										<TableHead className="text-right">Local</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{remittances.map((r) => (
										<TableRow key={r.remittanceId}>
											<TableCell className="tabular-nums">
												{new Date(r.remittedAt).toLocaleDateString()}
											</TableCell>
											<TableCell className="font-mono">
												{r.filingReference}
											</TableCell>
											<TableCell className="text-right">
												<Money cents={r.amountEur} currency="EUR" />
											</TableCell>
											<TableCell className="text-right text-muted-foreground">
												<Money cents={r.amountLocal} currency={r.currency} />
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
					</DetailPanel>
				</div>

				<div className="flex min-w-0 flex-col gap-4">
					<DetailPanel title="Amounts" icon={Wallet}>
						<SummaryGroup>
							<SummaryRow label="Supplies">
								<Money cents={declaration.suppliesVatEur} currency="EUR" />
							</SummaryRow>
							<SummaryRow label="Corrections">
								<Money cents={declaration.correctionsVatEur} currency="EUR" />
							</SummaryRow>
							<SummaryRow label="Total due" total>
								<Money cents={declaration.totalDueEur} currency="EUR" />
							</SummaryRow>
						</SummaryGroup>
					</DetailPanel>

					<MarkFiledPanel
						declarationId={declarationId}
						disabled={alreadyFiled}
						onSubmit={async (input) => {
							const [error] = await safe(
								markFiled.mutateAsync({ declarationId, ...input }),
							)
							if (isDefinedError(error)) {
								toast.error(
									`Could not mark filed: ${(error as { code: string }).code}`,
								)
								return false
							}
							if (error) {
								toast.error("Could not mark filed.")
								return false
							}
							toast.success("Marked filed.")
							return true
						}}
						busy={markFiled.isPending}
					/>
					<RecordPaymentPanel
						declarationId={declarationId}
						primary={alreadyFiled}
						onSubmit={async (input) => {
							const [error] = await safe(
								recordPayment.mutateAsync({ declarationId, ...input }),
							)
							if (isDefinedError(error)) {
								toast.error(
									`Could not record payment: ${(error as { code: string }).code}`,
								)
								return false
							}
							if (error) {
								toast.error("Could not record payment.")
								return false
							}
							toast.success("Payment recorded.")
							return true
						}}
						busy={recordPayment.isPending}
					/>
				</div>
			</div>
		</div>
	)
}

function LinePanel({
	title,
	lines,
}: {
	title: string
	lines: Array<{
		lineId: string
		country: string
		rate?: number
		supplyType?: string
		baseEur?: number
		vatEur?: number
		correctedPeriod?: string
		lineCount?: number
	}>
}) {
	return (
		<DetailPanel title={title} flush>
			{lines.length === 0 ? (
				<EmptyLine className="px-4 py-3">No lines.</EmptyLine>
			) : (
				<Table className={FLUSH_TABLE_CLASS}>
					<TableHeader>
						<TableRow>
							<TableHead>Country</TableHead>
							<TableHead>Type</TableHead>
							<TableHead className="text-right">Rate</TableHead>
							<TableHead>Corrected period</TableHead>
							<TableHead className="text-right">Base (EUR)</TableHead>
							<TableHead className="text-right">VAT (EUR)</TableHead>
							<TableHead className="text-right">Lines</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{lines.map((l) => (
							<TableRow key={l.lineId}>
								<TableCell>{l.country}</TableCell>
								<TableCell className="font-mono text-muted-foreground">
									{l.supplyType ?? ""}
								</TableCell>
								<TableCell className="text-right tabular-nums">
									{l.rate != null ? formatRate(l.rate) : ""}
								</TableCell>
								<TableCell className="font-mono">
									{l.correctedPeriod ?? ""}
								</TableCell>
								<TableCell className="text-right">
									{l.baseEur != null ? (
										<Money cents={l.baseEur} currency="EUR" />
									) : (
										<span className="text-meta">{EMPTY_VALUE}</span>
									)}
								</TableCell>
								<TableCell className="text-right">
									{l.vatEur != null ? (
										<Money cents={l.vatEur} currency="EUR" />
									) : (
										<span className="text-meta">{EMPTY_VALUE}</span>
									)}
								</TableCell>
								<TableCell className="text-right tabular-nums text-muted-foreground">
									{l.lineCount ?? ""}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</DetailPanel>
	)
}

/** A label above its control, for the narrow forms of the summary column. */
function StackedField({
	label,
	htmlFor,
	children,
}: {
	label: React.ReactNode
	htmlFor: string
	children: React.ReactNode
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<Label htmlFor={htmlFor} className="text-muted-foreground">
				{label}
			</Label>
			{children}
		</div>
	)
}

function MarkFiledPanel({
	declarationId,
	disabled,
	onSubmit,
	busy,
}: {
	declarationId: string
	disabled: boolean
	onSubmit: (input: {
		filingReference: string
		filedAt: Date
		notes?: string
	}) => Promise<boolean>
	busy: boolean
}) {
	const [filingReference, setFilingReference] = React.useState("")
	const [filedAt, setFiledAt] = React.useState(
		new Date().toISOString().slice(0, 10),
	)

	const handle = async () => {
		if (!filingReference.trim()) {
			toast.error("Filing reference is required.")
			return
		}
		const ok = await onSubmit({
			filingReference: filingReference.trim(),
			filedAt: new Date(filedAt),
		})
		if (ok) setFilingReference("")
	}

	return (
		<DetailPanel title="Mark filed" icon={FileCheck}>
			<DetailPanelSection>
				<StackedField
					label="Filing reference"
					htmlFor={`${declarationId}-filed-ref`}
				>
					<Input
						id={`${declarationId}-filed-ref`}
						value={filingReference}
						onChange={(e) => setFilingReference(e.target.value)}
						disabled={disabled}
					/>
				</StackedField>
				<StackedField label="Filed at" htmlFor={`${declarationId}-filed-at`}>
					<Input
						id={`${declarationId}-filed-at`}
						type="date"
						value={filedAt}
						onChange={(e) => setFiledAt(e.target.value)}
						disabled={disabled}
					/>
				</StackedField>
				<div className="pt-1">
					<Button
						variant={disabled ? "outline" : "default"}
						onClick={handle}
						disabled={disabled || busy}
					>
						{disabled ? "Already filed" : "Mark filed"}
					</Button>
				</div>
			</DetailPanelSection>
		</DetailPanel>
	)
}

function RecordPaymentPanel({
	declarationId,
	primary,
	onSubmit,
	busy,
}: {
	declarationId: string
	/** Makes the save button the page's primary action. */
	primary: boolean
	onSubmit: (input: {
		filingReference: string
		amountEur: number
		amountLocal: number
		currency: string
		remittedAt: Date
	}) => Promise<boolean>
	busy: boolean
}) {
	const [filingReference, setFilingReference] = React.useState("")
	const [amountEur, setAmountEur] = React.useState("")
	const [remittedAt, setRemittedAt] = React.useState(
		new Date().toISOString().slice(0, 10),
	)

	const handle = async () => {
		if (!filingReference.trim() || !amountEur) {
			toast.error("Reference and amount are required.")
			return
		}
		const cents = Math.round(Number(amountEur) * 100)
		const ok = await onSubmit({
			filingReference: filingReference.trim(),
			amountEur: cents,
			amountLocal: cents,
			currency: "EUR",
			remittedAt: new Date(remittedAt),
		})
		if (ok) {
			setFilingReference("")
			setAmountEur("")
		}
	}

	return (
		<DetailPanel title="Record payment" icon={Banknote}>
			<DetailPanelSection>
				<StackedField
					label="Filing reference"
					htmlFor={`${declarationId}-pay-ref`}
				>
					<Input
						id={`${declarationId}-pay-ref`}
						value={filingReference}
						onChange={(e) => setFilingReference(e.target.value)}
					/>
				</StackedField>
				<StackedField
					label="Amount (EUR)"
					htmlFor={`${declarationId}-pay-amount`}
				>
					<Input
						id={`${declarationId}-pay-amount`}
						type="number"
						step="0.01"
						value={amountEur}
						onChange={(e) => setAmountEur(e.target.value)}
					/>
				</StackedField>
				<StackedField label="Remitted at" htmlFor={`${declarationId}-pay-at`}>
					<Input
						id={`${declarationId}-pay-at`}
						type="date"
						value={remittedAt}
						onChange={(e) => setRemittedAt(e.target.value)}
					/>
				</StackedField>
				<div className="pt-1">
					<Button
						variant={primary ? "default" : "outline"}
						onClick={handle}
						disabled={busy}
					>
						Record payment
					</Button>
				</div>
			</DetailPanelSection>
		</DetailPanel>
	)
}
